
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserAlaresCategoryRankingEntity from 'Models/Security/Acl/UserAlaresCategoryRankingEntity';
import VisitorsAlaresCategoryRankingEntity from 'Models/Security/Acl/VisitorsAlaresCategoryRankingEntity';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IAlaresCategoryRankingEntityAttributes extends IModelAttributes {
	ranking: number;
	category: Enums.alaresCategory;
	enabled: boolean;


}

@entity('AlaresCategoryRankingEntity', 'Alares Category Ranking')
export default class AlaresCategoryRankingEntity extends Model
	implements IAlaresCategoryRankingEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserAlaresCategoryRankingEntity(),
		new VisitorsAlaresCategoryRankingEntity(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The default order by field when the collection is loaded .
	 */
	public get orderByField(): IOrderByCondition<Model> | undefined {
		// % protected region % [Modify the order by field here] off begin
		return {
			path: 'ranking',
			descending: false,
		};
		// % protected region % [Modify the order by field here] end
	}

	
	/**
	 * Determines the order of which the Alares Categories will be displayed
	 */
	@Validators.Required()
	@Validators.Integer()
	@Validators.Unique()
	@observable
	@attribute<AlaresCategoryRankingEntity, number>()
	@CRUD({
		name: 'Ranking',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseInteger,
	})
	public ranking: number;
	

	
	@Validators.Required()
	@Validators.Unique()
	@observable
	@attribute<AlaresCategoryRankingEntity, Enums.alaresCategory>()
	@CRUD({
		name: 'Category',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.alaresCategoryOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.alaresCategoryOptions),
		displayFunction: (attr: Enums.alaresCategory) => Enums.alaresCategoryOptions[attr],
	})
	public category: Enums.alaresCategory;
	

	
	/**
	 * When enabled, users will be notified on this Alares Category
	 */
	@Validators.Required()
	@observable
	@attribute<AlaresCategoryRankingEntity, boolean>()
	@CRUD({
		name: 'Enabled',
		displayType: 'checkbox',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseBoolean,
		displayFunction: attr => attr ? 'True' : 'False',
	})
	public enabled: boolean = false;


	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAlaresCategoryRankingEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAlaresCategoryRankingEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.ranking !== undefined) {
				this.ranking = attributes.ranking;
			}
			if (attributes.category !== undefined) {
				this.category = attributes.category;
			}
			if (attributes.enabled !== undefined) {
				this.enabled = attributes.enabled;
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AlaresCategoryRankingEntity.prototype, 'created');
CRUD(modifiedAttr)(AlaresCategoryRankingEntity.prototype, 'modified');
